import * as React from 'react';

import { connect, useDispatch } from 'react-redux';
import { Translate } from 'react-localize-redux';

import styled from 'styled-components';

import AppState from '@webapp/store/state';
import { ListEarnings } from '@webapp/store/earnings/action-creators';

import SummaryDetailsCtrl from '@webapp/controllers/summary';
import HistoryDetailItem, { DetailItemHistory } from './history-detail-item';
import moment from 'moment';
import { PayoutHistory } from '@services/royalties/types/payout-history';
import DineroFactory from 'dinero.js';
import { EarningsResponse } from '@services/earnings/types/earnings-response';
import { AnyPayoutDestination } from '@services/crypto-payments/types/api/payout-destination';

interface WalletHistoryPageProps {
  className?: string;
  ownerId: string;
  allTime?: boolean;
  payout: PayoutHistory[];

  pendingEntries: EarningsResponse[];
  completedEntries: EarningsResponse[];
  destinationAddressEntries?: AnyPayoutDestination[];
}

const mapStateToProps = (state: AppState) => ({
  ownerId: state.user.profile?.id!,
  payout: state.royalties.payouts,
  pendingEntries: state.earnings.data.OWNER.filter(
    (entry) => Number(entry.eligibleDate) > Number(new Date())
  ),
  completedEntries: state.earnings.data.OWNER.filter(
    (entry) => Number(entry.eligibleDate) <= Number(new Date())
  ),
  destinationAddressEntries: [],
});

export const WalletHistoryPage: React.FC<WalletHistoryPageProps> = ({
  className,
  ownerId,
  payout,
  pendingEntries,
  completedEntries,
  destinationAddressEntries,
}) => {
  const dispatch = useDispatch();
  const [selectedDate, setSelectedDate] = React.useState<Date>();
  const [hasDateChange, setHasDateChange] = React.useState(false);
  const [datePicker, setDatePickker] = React.useState('All Time');
  const [allTime, setAllTime] = React.useState(true);
  const [walletPage, setWalletPage] = React.useState<boolean>(true);

  const [pendingEarnings, setPendingEarnings] = React.useState<
    DetailItemHistory[]
  >([]);
  const [completedEarnings, setCompletedEarnings] = React.useState<
    DetailItemHistory[]
  >([]);

  const formatAmount = (
    amount: Dinero.DineroObject,
    negativeAmount?: boolean
  ) => {
    return DineroFactory({
      amount: negativeAmount ? amount['amount'] * -1 : amount['amount'],
      currency: amount['currency'],
      precision: amount['precision'],
    });
  };

  const sortHistoryDetails = (
    history: DetailItemHistory[]
  ): DetailItemHistory[] => {
    const result: DetailItemHistory[] = [];
    history.reduce((res, value) => {
      if (!res[`${value.date}-${value.type}`]) {
        res[`${value.date}-${value.type}`] = { ...value };
        result.push(res[`${value.date}-${value.type}`]);
      } else {
        res[`${value.date}-${value.type}`].amount = res[
          `${value.date}-${value.type}`
        ].amount.add(value.amount);
      }
      return res;
    }, {});
    return result.sort((a, b) => Number(b.date) - Number(a.date));
  };

  React.useEffect(() => {
    const getDestinationAddress = (eligibleDate: Date, account?: string) => {
      if (account) {
        let destinationTag = account.match(/\(([^)]+)\)/);
        if (destinationTag) {
          let destinationAddress = destinationAddressEntries?.find(
            (acc) =>
              acc.destinationTag ===
              (destinationTag ? destinationTag[1] : account)
          )?.destinationAddress;
          return destinationAddress
            ? destinationAddress
            : `Monthly Payout (${moment(eligibleDate).format(`MMMM`)})`;
        } else {
          return `Monthly Payout (${moment(eligibleDate).format(`MMMM`)})`;
        }
      } else {
        return `Monthly Payout (${moment(eligibleDate).format(`MMMM`)})`;
      }
    };
    let selectedMonth: number = 0;
    let selectedYear: number = 0;
    if (selectedDate) {
      selectedMonth = moment(selectedDate).month() + 1;
      selectedYear = moment(selectedDate).year();
    }

    const data: DetailItemHistory[] = payout
      .filter((pay) =>
        selectedMonth && selectedYear
          ? pay.month.replace(/^(0+)/g, '') === selectedMonth.toString() &&
            pay.year === selectedYear.toString() &&
            pay.payoutStatus === 'pending'
          : pay.payoutStatus && pay.payoutStatus === 'pending'
      )
      .map((pay) => ({
        label: pay.destination || 'trac Wallet',
        description: getDestinationAddress(pay.created_at, pay.destination),
        amount: formatAmount(pay?.amount.total, !!pay.destination),
        date: new Date(pay.created_at),
        type: 'payout',
      }));
    const pendingEntriesData: DetailItemHistory[] = pendingEntries
      .filter((data) =>
        selectedMonth && selectedYear
          ? moment(data.eligibleDate).month() + 1 === selectedMonth &&
            moment(data.eligibleDate).year() === selectedYear
          : true
      )
      .map((data) => ({
        label: `Earnings from ${
          ['Streaming', 'Merch'].includes(data.type) ? data.type : 'Streaming'
        }`,
        description: `Monthly Payout (${moment(data.eligibleDate).format(
          'MMMM'
        )})`,
        amount: data.total,
        date: data.eligibleDate,
        type: 'earning',
        artistIds: data.entries.map((i) => i.artistId),
      }));

    setPendingEarnings(sortHistoryDetails(pendingEntriesData.concat(data)));
  }, [payout, selectedDate, pendingEntries, destinationAddressEntries]);

  React.useEffect(() => {
    const getDestinationAddress = (eligibleDate: Date, account?: string) => {
      if (account) {
        let destinationTag = account.match(/\(([^)]+)\)/);
        if (destinationTag) {
          let destinationAddress = destinationAddressEntries?.find(
            (acc) =>
              acc.destinationTag ===
              (destinationTag ? destinationTag[1] : account)
          )?.destinationAddress;
          return destinationAddress
            ? destinationAddress
            : `Monthly Payout (${moment(eligibleDate).format(`MMMM`)})`;
        } else {
          return `Monthly Payout (${moment(eligibleDate).format(`MMMM`)})`;
        }
      } else {
        return `Monthly Payout (${moment(eligibleDate).format(`MMMM`)})`;
      }
    };
    let selectedMonth: number = 0;
    let selectedYear: number = 0;
    if (selectedDate) {
      selectedMonth = moment(selectedDate).month() + 1;
      selectedYear = moment(selectedDate).year();
    }
    const data: DetailItemHistory[] = payout
      .filter((pay) =>
        selectedMonth && selectedYear
          ? pay.month.replace(/^(0+)/g, '') === selectedMonth.toString() &&
            pay.year === selectedYear.toString() &&
            pay.payoutStatus !== 'pending'
          : pay.payoutStatus
          ? pay.payoutStatus !== 'pending'
          : true
      )
      .map((pay) => ({
        label: pay.destination || 'trac Wallet',
        description: getDestinationAddress(pay.created_at, pay.destination),
        amount: formatAmount(pay?.amount.total, !!pay.destination),
        date: new Date(pay.created_at),
        status: pay.payoutStatus,
        type: 'payout',
      }));

    const completedEntriesData: DetailItemHistory[] = completedEntries
      .filter((data) =>
        selectedMonth && selectedYear
          ? moment(data.eligibleDate).month() + 1 === selectedMonth &&
            moment(data.eligibleDate).year() === selectedYear
          : true
      )
      .map((data) => ({
        label: `Earnings from ${
          ['Streaming', 'Merch'].includes(data.type) ? data.type : 'Streaming'
        }`,
        description: `Monthly Payout (${moment(data.eligibleDate).format(
          `MMMM`
        )})`,
        amount: data.total,
        date: data.eligibleDate,
        type: 'earning',
        artistIds: data.entries.map((i) => i.artistId),
      }));

    setCompletedEarnings(sortHistoryDetails(completedEntriesData.concat(data)));
  }, [payout, selectedDate, completedEntries, destinationAddressEntries]);

  const dsp = '';
  const handleDateChange = (value: Date) => {
    setDatePickker('');
    setAllTime(false);
    setHasDateChange(true);
    setSelectedDate(value);
    //fetch earnings
    if (ownerId) {
      dispatch(
        new ListEarnings(
          'OWNER',
          ownerId as string,
          dsp,
          '',
          new Date(value).getTime()
        )
      );
    }
  };

  // TODO: 36pn43q - This HandleDownload needs to be integrated.
  const handleDownload = () => {
    return;
  };

  return (
    <div className={className}>
      <SummaryDetailsCtrl
        sectionTitle={<Translate id="USER_PROFILE.WALLET.WALLET_NAV" />}
        selectedDate={selectedDate}
        hasDateChange={hasDateChange}
        handleDownload={handleDownload}
        handleDateChange={handleDateChange}
        walletHistory={true}
        datePickerValue={datePicker}
        allTime={allTime}
        walletPage={walletPage}
      >
        <HistoryDetailItem
          walletPage={walletPage}
          setWalletPage={setWalletPage}
          pendingEarnings={pendingEarnings}
          completedEarnings={completedEarnings}
        />
      </SummaryDetailsCtrl>
    </div>
  );
};

export default connect(mapStateToProps)(styled(WalletHistoryPage)``);
